import { Component, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { LoadingComponent } from '@digi-america/shared';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { ReportService } from '../../services/reports.service';
import { SessionService } from '../../services/session.service';
import { QuoteStatus } from '../../types/dai-server/quotes/enums';
import { ResponseQuoteReport, ResponseQuoteReportCreator, ResponseQuoteReportProduct, ResponseQuoteReportStatus } from '../../types/dai-server/reports/report-responses';

type RangePreset = 'last30' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'yearToDate' | 'custom';

type SortState<K extends string> = { key: K; descending: boolean };
/** A column key, or a quote status for the creators table's per-status columns */
type CreatorSortKey = 'creatorName' | 'quoteCount' | 'subTotalAmount' | 'totalAmount' | QuoteStatus;
type ProductSortKey = 'sku' | 'name' | 'productCategory' | 'quoteCount' | 'totalQuantity' | 'totalAmount';

@Component({
  selector: 'app-reports',
  imports: [CommonModule, FormsModule, CurrencyPipe, DecimalPipe, LoadingComponent, NgbPagination],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  standalone: true
})
export class ReportsComponent implements OnInit {
  readonly presets: { id: RangePreset; label: string }[] = [
    { id: 'last30', label: 'Last 30 days' },
    { id: 'thisMonth', label: 'This month' },
    { id: 'lastMonth', label: 'Last month' },
    { id: 'thisQuarter', label: 'This quarter' },
    { id: 'yearToDate', label: 'Year to date' },
    { id: 'custom', label: 'Custom' },
  ];
  readonly allStatuses = Object.values(QuoteStatus);
  readonly productPageSize = 25;

  preset: RangePreset = 'thisMonth';
  from: string = '';
  to: string = '';
  selectedStatuses: QuoteStatus[] = [];

  report?: ResponseQuoteReport;
  loading = false;
  error?: string;
  private requestId = 0;

  creatorSort: SortState<CreatorSortKey> = { key: 'totalAmount', descending: true };
  productSort: SortState<ProductSortKey> = { key: 'totalAmount', descending: true };
  productPage = 1;

  // Sorted copies and totals, rebuilt when the report or a sort changes
  creators: ResponseQuoteReportCreator[] = [];
  products: ResponseQuoteReportProduct[] = [];
  creatorTotals = { quoteCount: 0, subTotalAmount: 0, totalAmount: 0, statuses: {} as Record<string, number> };
  productTotals = { totalQuantity: 0, totalAmount: 0 };

  constructor(private _reports: ReportService, private _session: SessionService) { }

  ngOnInit(): void {
    this.applyPreset();
  }

  /** The server applies report_all when the token has both, and does not say which scope it used */
  scopeLabel(): string {
    return this._session.hasPermission('quote:report_all') ? 'All quotes' : 'Your team';
  }

  applyPreset() {
    if (this.preset === 'custom') {
      return;
    }

    const today = new Date();
    let from: Date;
    let to: Date = today;
    switch (this.preset) {
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'lastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        from = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        break;
      case 'yearToDate':
        from = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
    }

    this.from = this.toDateString(from);
    this.to = this.toDateString(to);
    this.load();
  }

  onDateChange() {
    this.preset = 'custom';
    this.load();
  }

  isStatusSelected(status: QuoteStatus): boolean {
    return this.selectedStatuses.includes(status);
  }

  toggleStatus(status: QuoteStatus) {
    this.selectedStatuses = this.isStatusSelected(status)
      ? this.selectedStatuses.filter(s => s !== status)
      : [...this.selectedStatuses, status];
    this.load();
  }

  async load() {
    if (!this.from || !this.to) {
      return;
    }
    if (this.from > this.to) {
      this.error = 'The start date must be on or before the end date.';
      return;
    }

    // Filters can change while a report is loading, only the latest request is shown
    const requestId = ++this.requestId;
    this.loading = true;
    this.error = undefined;
    try {
      const report = await firstValueFrom(this._reports.getQuoteReport(this.from, this.to, this.selectedStatuses));
      if (requestId === this.requestId) {
        this.setReport(report);
      }
    } catch (e) {
      if (requestId !== this.requestId) {
        return;
      }
      this.report = undefined;
      this.error = e instanceof HttpErrorResponse && typeof e.error?.error === 'string'
        ? e.error.error
        : 'The report could not be loaded, please try again.';
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
      }
    }
  }

  private setReport(report: ResponseQuoteReport) {
    this.report = report;
    this.productPage = 1;

    this.creatorTotals = { quoteCount: 0, subTotalAmount: 0, totalAmount: 0, statuses: {} };
    for (const creator of report.creators) {
      this.creatorTotals.quoteCount += creator.quoteCount;
      this.creatorTotals.subTotalAmount += creator.subTotalAmount;
      this.creatorTotals.totalAmount += creator.totalAmount;
      for (const status of creator.statuses) {
        this.creatorTotals.statuses[status.status] = (this.creatorTotals.statuses[status.status] ?? 0) + status.quoteCount;
      }
    }

    this.productTotals = { totalQuantity: 0, totalAmount: 0 };
    for (const product of report.products) {
      this.productTotals.totalQuantity += product.totalQuantity;
      this.productTotals.totalAmount += product.totalAmount;
    }

    this.sortCreators();
    this.sortProducts();
  }

  /** Clicking the sorted column flips it, a new column starts high to low for numbers and A to Z for text */
  private nextSort<K extends string>(current: SortState<K>, key: K, textKeys: K[]): SortState<K> {
    if (current.key === key) {
      return { key, descending: !current.descending };
    }
    return { key, descending: !textKeys.includes(key) };
  }

  sortCreatorsBy(key: CreatorSortKey) {
    this.creatorSort = this.nextSort(this.creatorSort, key, ['creatorName']);
    this.sortCreators();
  }

  sortProductsBy(key: ProductSortKey) {
    this.productSort = this.nextSort(this.productSort, key, ['sku', 'name', 'productCategory']);
    this.productPage = 1;
    this.sortProducts();
  }

  private sortCreators() {
    const { key, descending } = this.creatorSort;
    const isStatus = (this.allStatuses as string[]).includes(key);
    const value = (c: ResponseQuoteReportCreator): string | number => isStatus
      ? this.statusCount(c.statuses, key as QuoteStatus)
      : c[key as 'creatorName' | 'quoteCount' | 'subTotalAmount' | 'totalAmount'];
    this.creators = this.sorted(this.report?.creators ?? [], value, descending);
  }

  private sortProducts() {
    const { key, descending } = this.productSort;
    this.products = this.sorted(this.report?.products ?? [], p => p[key], descending);
  }

  private sorted<T>(items: T[], value: (item: T) => string | number, descending: boolean): T[] {
    const direction = descending ? -1 : 1;
    return [...items].sort((a, b) => {
      const x = value(a), y = value(b);
      const result = typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      return result * direction;
    });
  }

  sortIndicator(sort: SortState<string>, key: string): string {
    return sort.key === key ? (sort.descending ? '▼' : '▲') : '';
  }

  ariaSort(sort: SortState<string>, key: string): string | null {
    return sort.key === key ? (sort.descending ? 'descending' : 'ascending') : null;
  }

  pagedProducts(): ResponseQuoteReportProduct[] {
    const start = (this.productPage - 1) * this.productPageSize;
    return this.products.slice(start, start + this.productPageSize);
  }

  statusCount(statuses: ResponseQuoteReportStatus[], status: QuoteStatus): number {
    return statuses.find(s => s.status === status)?.quoteCount ?? 0;
  }

  statusClass(status: QuoteStatus): string {
    switch (status) {
      case QuoteStatus.open: return 'bg-primary';
      case QuoteStatus.finalized: return 'bg-info text-dark';
      case QuoteStatus.ordered: return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  private toDateString(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
